import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShield,
  faPlus,
  faSpinner,
  faCheck,
  faTimes,
  faUndo,
  faArrowRotateRight,
  faChevronDown,
  faChevronUp,
} from '@fortawesome/free-solid-svg-icons';
import { inspectionService } from '@/services/inspection.service';
import { approvalRequestTypeService } from '@/services/approval-request-type.service';
import type { EvidenceSelectionItem } from '@/services/authorization-case.service';
import { Modal } from '@/components/shared/Modal';
import { Button } from '@/components/ui/button';
import { toast } from '@/lib/swal';
import { EvidenceSelectionPicker } from '@/features/authorization/components/EvidenceSelectionPicker';

interface Props {
  inspectionId: string;
  findingId: string;
  canCreate: boolean;
  canRespond: boolean;
  canResubmit?: boolean;
}

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

function fmtDateTime(v: string): string {
  return new Date(v).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const STATUS_BADGE: Record<string, string> = {
  PENDING: 'bg-amber-500/10 text-amber-700',
  APPROVED: 'bg-green-500/10 text-green-700',
  REJECTED: 'bg-red-500/10 text-red-700',
  EXPIRED: 'bg-muted text-muted-foreground',
};

const STAGE_BADGE: Record<string, string> = {
  PENDING_OPS_REVIEW: 'bg-amber-500/10 text-amber-700',
  RETURNED_TO_ENGINEER: 'bg-orange-500/10 text-orange-700',
  FORWARDED_TO_CUSTOMER: 'bg-blue-500/10 text-blue-700',
  CUSTOMER_PENDING: 'bg-blue-500/10 text-blue-700',
  RESOLVED: 'bg-muted text-muted-foreground',
};

const STAGE_LABEL: Record<string, string> = {
  PENDING_OPS_REVIEW: 'Pending OPS Review',
  RETURNED_TO_ENGINEER: 'Returned to Engineer',
  FORWARDED_TO_CUSTOMER: 'Forwarded to Customer',
  CUSTOMER_PENDING: 'Customer Pending',
  RESOLVED: 'Resolved',
};

const ACTION_LABEL: Record<string, string> = {
  REQUESTED: 'Requested',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  RETURNED: 'Returned to Engineer',
  COMMENTED: 'Commented',
};

export function AuthorizationSection({
  inspectionId,
  findingId,
  canCreate,
  canRespond,
  canResubmit = false,
}: Props) {
  const qc = useQueryClient();

  const [requestOpen, setRequestOpen] = useState(false);
  const [respondOpen, setRespondOpen] = useState<string | null>(null);
  const [resubmitOpen, setResubmitOpen] = useState<string | null>(null);

  // Request form state
  const [reason, setReason] = useState('');
  const [typeMode, setTypeMode] = useState<'catalog' | 'custom'>('catalog');
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [customTypeName, setCustomTypeName] = useState('');
  const [evidence, setEvidence] = useState<EvidenceSelectionItem[]>([]);

  // Respond form state
  const [respondAction, setRespondAction] = useState<'APPROVED' | 'REJECTED' | 'RETURNED'>(
    'APPROVED',
  );
  const [respondComment, setRespondComment] = useState('');

  // Resubmit form state
  const [resubmitReason, setResubmitReason] = useState('');

  const [expandedHistory, setExpandedHistory] = useState<Set<string>>(new Set());

  const { data, isLoading } = useQuery({
    queryKey: ['finding-authorizations', inspectionId, findingId],
    queryFn: () => inspectionService.getAuthorizations(inspectionId, findingId, { pageSize: 20 }),
    staleTime: 15_000,
  });

  const { data: approvalTypes } = useQuery({
    queryKey: ['approval-request-types-all'],
    queryFn: () => approvalRequestTypeService.listAllActive(),
    staleTime: 60_000,
    enabled: requestOpen,
  });

  const createMutation = useMutation({
    mutationFn: () => {
      const payload: {
        reason: string;
        approvalRequestTypeId?: string;
        isCustom?: boolean;
        customApprovalName?: string;
        evidence?: EvidenceSelectionItem[];
      } = { reason, evidence: evidence.length > 0 ? evidence : undefined };

      if (typeMode === 'catalog') {
        payload.approvalRequestTypeId = selectedTypeId;
      } else {
        payload.isCustom = true;
        payload.customApprovalName = customTypeName.trim();
      }

      return inspectionService.createAuthorization(inspectionId, findingId, payload);
    },
    onSuccess: () => {
      setRequestOpen(false);
      setReason('');
      setTypeMode('catalog');
      setSelectedTypeId('');
      setCustomTypeName('');
      setEvidence([]);
      void qc.invalidateQueries({ queryKey: ['finding-authorizations', inspectionId, findingId] });
      toast.success('Authorization requested');
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to request authorization')),
  });

  const respondMutation = useMutation({
    mutationFn: (authId: string) =>
      inspectionService.respondAuthorization(inspectionId, findingId, authId, {
        action: respondAction,
        comment: respondComment || undefined,
      }),
    onSuccess: () => {
      setRespondOpen(null);
      setRespondComment('');
      void qc.invalidateQueries({ queryKey: ['finding-authorizations', inspectionId, findingId] });
      toast.success('Response recorded');
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to respond')),
  });

  const resubmitMutation = useMutation({
    mutationFn: (authId: string) =>
      inspectionService.resubmitAuthorization(inspectionId, findingId, authId, {
        updatedReason: resubmitReason.trim() || undefined,
      }),
    onSuccess: () => {
      setResubmitOpen(null);
      setResubmitReason('');
      void qc.invalidateQueries({ queryKey: ['finding-authorizations', inspectionId, findingId] });
      toast.success('Authorization resubmitted for OPS review');
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to resubmit')),
  });

  const authorizations = data?.data ?? [];
  const hasPending = authorizations.some((a) => a.status === 'PENDING');

  const canSubmitRequest =
    reason.trim().length > 0 &&
    ((typeMode === 'catalog' && selectedTypeId !== '') ||
      (typeMode === 'custom' && customTypeName.trim().length > 0)) &&
    !createMutation.isPending;

  function toggleHistory(id: string) {
    setExpandedHistory((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function getTypeName(auth: {
    approvalRequestTypeName: string | null;
    isCustom: boolean;
    customApprovalName: string | null;
  }): string | null {
    if (auth.approvalRequestTypeName) return auth.approvalRequestTypeName;
    if (auth.isCustom && auth.customApprovalName) return `${auth.customApprovalName} (Custom)`;
    return null;
  }

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-5 py-3">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faShield} className="h-3.5 w-3.5 text-muted-foreground" />
          <h2 className="text-sm font-semibold text-foreground">Authorizations</h2>
          {data && <span className="text-xs text-muted-foreground">({data.total})</span>}
        </div>
        {canCreate && !hasPending && (
          <button
            onClick={() => setRequestOpen(true)}
            className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition hover:bg-primary/90"
          >
            <FontAwesomeIcon icon={faPlus} className="h-3 w-3" />
            Request
          </button>
        )}
      </div>

      <div className="px-5 py-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
            Loading…
          </div>
        ) : authorizations.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No authorization requests for this finding.
          </p>
        ) : (
          <div className="space-y-4">
            {authorizations.map((auth) => {
              const isHistoryExpanded = expandedHistory.has(auth.id);
              const stage = auth.workflowStage;
              const isReturned = stage === 'RETURNED_TO_ENGINEER';
              const typeName = getTypeName(auth);

              return (
                <div key={auth.id} className="rounded-lg border border-border bg-muted/20 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[auth.status] ?? 'bg-muted text-muted-foreground'}`}
                        >
                          {auth.status}
                        </span>
                        {stage && (
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs font-medium ${STAGE_BADGE[stage] ?? 'bg-muted text-muted-foreground'}`}
                          >
                            {STAGE_LABEL[stage] ?? stage}
                          </span>
                        )}
                        {typeName && (
                          <span className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground">
                            {typeName}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-foreground">{auth.reason}</p>
                      <p className="text-xs text-muted-foreground">
                        Requested by {auth.requestedByName} · {fmtDateTime(auth.requestedAt)}
                      </p>
                      {auth.responseNotes && (
                        <p className="text-sm text-foreground">
                          <span className="text-xs text-muted-foreground">Response: </span>
                          {auth.responseNotes}
                        </p>
                      )}
                    </div>

                    <div className="flex shrink-0 flex-col gap-1.5">
                      {canRespond && auth.status === 'PENDING' && !isReturned && (
                        <button
                          onClick={() => {
                            setRespondOpen(auth.id);
                            setRespondAction('APPROVED');
                            setRespondComment('');
                          }}
                          className="rounded-md border border-border px-2.5 py-1.5 text-xs text-foreground transition hover:bg-muted"
                        >
                          Respond
                        </button>
                      )}
                      {canResubmit && isReturned && (
                        <button
                          onClick={() => {
                            setResubmitOpen(auth.id);
                            setResubmitReason('');
                          }}
                          className="flex items-center gap-1 rounded-md border border-orange-400/60 bg-orange-50 px-2.5 py-1.5 text-xs font-medium text-orange-700 transition hover:bg-orange-100"
                        >
                          <FontAwesomeIcon icon={faArrowRotateRight} className="h-2.5 w-2.5" />
                          Resubmit
                        </button>
                      )}
                    </div>
                  </div>

                  {auth.history.length > 0 && (
                    <div className="mt-2">
                      <button
                        type="button"
                        onClick={() => toggleHistory(auth.id)}
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition"
                      >
                        <FontAwesomeIcon
                          icon={isHistoryExpanded ? faChevronUp : faChevronDown}
                          className="h-2.5 w-2.5"
                        />
                        {isHistoryExpanded ? 'Hide' : 'Show'} history ({auth.history.length})
                      </button>
                      {isHistoryExpanded && (
                        <div className="mt-2 border-t border-border pt-2 space-y-1.5">
                          {auth.history.map((h) => (
                            <div key={h.id} className="flex items-start gap-2 text-xs">
                              <span className="mt-0.5 rounded bg-muted px-1.5 py-0.5 font-medium text-muted-foreground">
                                {ACTION_LABEL[h.action] ?? h.action}
                              </span>
                              <span className="text-muted-foreground">
                                {h.actorName} ({h.actorRoleName}) · {fmtDateTime(h.createdAt)}
                                {h.comment && <span className="ml-1 italic">"{h.comment}"</span>}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Request modal ─────────────────────────────────────────────────── */}
      <Modal open={requestOpen} onClose={() => setRequestOpen(false)} title="Request Authorization">
        <div className="space-y-4">
          {/* Approval type picker — classification is mandatory */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-foreground">
              Approval Type <span className="text-destructive">*</span>
            </label>
            <div className="flex gap-2">
              {(['catalog', 'custom'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => {
                    setTypeMode(mode);
                    setSelectedTypeId('');
                    setCustomTypeName('');
                  }}
                  className={`rounded-md border px-3 py-1.5 text-xs font-medium transition ${typeMode === mode ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:border-foreground/40'}`}
                >
                  {mode === 'catalog' ? 'Select from list' : 'Other / Custom'}
                </button>
              ))}
            </div>
          </div>

          {typeMode === 'catalog' && (
            <div>
              <label className="mb-1 block text-xs font-medium text-foreground">
                Approval Request Type <span className="text-destructive">*</span>
              </label>
              <select
                value={selectedTypeId}
                onChange={(e) => setSelectedTypeId(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">— Select —</option>
                {(approvalTypes ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {typeMode === 'custom' && (
            <div>
              <label className="mb-1 block text-xs font-medium text-foreground">
                Custom Approval Name <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={customTypeName}
                onChange={(e) => setCustomTypeName(e.target.value)}
                maxLength={200}
                placeholder="e.g. OEM Engineering Approval"
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs font-medium text-foreground">
              Reason <span className="text-destructive">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={2000}
              rows={4}
              placeholder="Describe why authorization is needed to proceed with this finding…"
              className="w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <p className="mt-0.5 text-xs text-muted-foreground">{reason.length}/2000</p>
          </div>

          <EvidenceSelectionPicker
            inspectionId={inspectionId}
            selectedItems={evidence}
            onChange={setEvidence}
          />

          <div className="flex justify-end gap-2 pt-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setRequestOpen(false);
                setEvidence([]);
              }}
            >
              Cancel
            </Button>
            <Button size="sm" disabled={!canSubmitRequest} onClick={() => createMutation.mutate()}>
              {createMutation.isPending && (
                <FontAwesomeIcon icon={faSpinner} className="mr-1.5 h-3 w-3 animate-spin" />
              )}
              Submit Request
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Respond modal ─────────────────────────────────────────────────── */}
      {respondOpen && (
        <Modal open={true} onClose={() => setRespondOpen(null)} title="Respond to Authorization">
          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-xs font-medium text-foreground">Action</label>
              <div className="grid grid-cols-3 gap-2">
                {(
                  [
                    {
                      action: 'APPROVED',
                      label: 'Approve',
                      icon: faCheck,
                      cls: 'border-green-500/50 text-green-700 hover:bg-green-500/5',
                    },
                    {
                      action: 'REJECTED',
                      label: 'Reject',
                      icon: faTimes,
                      cls: 'border-red-500/50 text-red-700 hover:bg-red-500/5',
                    },
                    {
                      action: 'RETURNED',
                      label: 'Return to Engineer',
                      icon: faUndo,
                      cls: 'border-amber-500/50 text-amber-700 hover:bg-amber-500/5',
                    },
                  ] as const
                ).map(({ action, label, icon, cls }) => (
                  <button
                    key={action}
                    onClick={() => setRespondAction(action)}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition ${cls} ${respondAction === action ? 'ring-2 ring-ring' : ''}`}
                  >
                    <FontAwesomeIcon icon={icon} className="h-3.5 w-3.5" />
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-foreground">
                Comment{' '}
                {respondAction === 'RETURNED' || respondAction === 'REJECTED'
                  ? '(recommended)'
                  : '(optional)'}
              </label>
              <textarea
                value={respondComment}
                onChange={(e) => setRespondComment(e.target.value)}
                maxLength={5000}
                rows={3}
                placeholder="Add context for your response…"
                className="w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" size="sm" onClick={() => setRespondOpen(null)}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={respondMutation.isPending}
                onClick={() => respondMutation.mutate(respondOpen)}
              >
                {respondMutation.isPending && (
                  <FontAwesomeIcon icon={faSpinner} className="mr-1.5 h-3 w-3 animate-spin" />
                )}
                Confirm
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Resubmit modal ────────────────────────────────────────────────── */}
      {resubmitOpen && (
        <Modal open={true} onClose={() => setResubmitOpen(null)} title="Resubmit Authorization">
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This authorization was returned to you. You may update the reason before resubmitting.
            </p>
            <div>
              <label className="mb-1 block text-xs font-medium text-foreground">
                Updated reason{' '}
                <span className="text-muted-foreground font-normal">
                  (optional — leave blank to keep original)
                </span>
              </label>
              <textarea
                value={resubmitReason}
                onChange={(e) => setResubmitReason(e.target.value)}
                maxLength={2000}
                rows={4}
                placeholder="Update your reason if needed (min 20 chars if provided)…"
                className="w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" size="sm" onClick={() => setResubmitOpen(null)}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={resubmitMutation.isPending}
                onClick={() => resubmitMutation.mutate(resubmitOpen)}
              >
                {resubmitMutation.isPending && (
                  <FontAwesomeIcon icon={faSpinner} className="mr-1.5 h-3 w-3 animate-spin" />
                )}
                Resubmit
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
